import { fundingOptions } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/consts/fundingOptions";
import {
  FICO_SCORE_OPTIONS,
  OwFFormFieldKey,
  OwFFormFieldType,
} from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/consts/index";
import {
  getVisibleFieldKeys,
  showFundingSources,
  useBuildResidentialAddressSchema,
} from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/helpers";
import {
  IOwFFormFieldSchema,
  OwFFormFieldValueType,
} from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/OwFFormField";
import { useGetOwFApplicationStoreType } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useGetOwFApplicationStoreType";
import { useIsNMIApplication } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsNMIApplication";
import { useIsSoftCreditCheckApplication } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useIsSoftCreditCheckApplication";
import { useOwFIDVTierLogic } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useOwFIDVTierLogic";
import { useValidatePartialFormData } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useValidatePartialFormData";
import { useNationality } from "@faire/retailer-visitor-shared/hooks/retailer/useNationality";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { DEPRECATED_backwardsCompatibleValues } from "@faire/web--source/common/localization/localize";
import { formatMoney, RoundingStrategy } from "@faire/web--source/common/money";
import { isNotNull } from "@faire/web--source/common/typescriptUtils";
import { useQuery as useCreditCardLimitRangesQuery } from "@faire/web-api--source/endpoints/www/api/launch-with-faire/credit-card-limit-ranges/get-hooks";
import { useQuery as useFundingAmountRangesQuery } from "@faire/web-api--source/endpoints/www/api/launch-with-faire/funding-amount-ranges/get-hooks";
import { useQuery as usePreferredTermsAmountRangesQuery } from "@faire/web-api--source/endpoints/www/api/launch-with-faire/preferred-terms-amount-ranges/get-hooks";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { IRetailerLaunchWithFaireApplication } from "@faire/web-api--source/indigofair/data/IRetailerLaunchWithFaireApplication";

const CREDIT_CHECK_PREFERRED_TERMS_LIMIT_THRESHOLD = IFaireMoney.build({
  amount_cents: 5000 * 100,
  currency: "USD",
});

interface IUseFinancialInfo {
  formData: IRetailerLaunchWithFaireApplication;
  setValue: (
    fieldName: OwFFormFieldKey,
    newValue: OwFFormFieldValueType
  ) => void;
  isResidentialAddressFormVisible: boolean;
  toggleResidentialAddressForm: () => void;
  isSignUp?: boolean;
}

export const useFinancialInfo = ({
  formData,
  setValue,
  isResidentialAddressFormVisible,
  toggleResidentialAddressForm,
  isSignUp,
}: IUseFinancialInfo) => {
  const { strictLocalize } = useStrictLocalization();
  const { retailerCountry, isAmerican, isEurope } = useNationality();
  const { retailer } = useRetailer();
  const { buildResidentialAddressSchema } = useBuildResidentialAddressSchema();
  const { validatePartialFormData } = useValidatePartialFormData();
  const { isSoftCreditCheckApplication } = useIsSoftCreditCheckApplication();
  const { isAnyTierActive } = useOwFIDVTierLogic();
  const { isNMIApplication } = useIsNMIApplication();
  const { owfStoreType } = useGetOwFApplicationStoreType();

  const isRetailerOther =
    retailer?.business_category === IRetailer.BusinessCategory.BUSINESS_OTHER;
  const owfFormStoreType = isNMIApplication
    ? owfStoreType
    : formData.best_store_type;
  const retailerBusinessType = isRetailerOther
    ? (owfFormStoreType ?? IRetailer.BusinessCategory.ONLINE_ONLY)
    : retailer?.business_category;
  const isBrickAndMortar =
    retailerBusinessType === IRetailer.BusinessCategory.BRICK_AND_MORTAR;
  const requireCreditReport: boolean = (() => {
    const minAmountCents =
      formData.preferred_terms_amount_min?.amount_cents ?? 0;
    const maxAmountCents =
      formData.preferred_terms_amount_max?.amount_cents ?? 0;
    const threshold =
      CREDIT_CHECK_PREFERRED_TERMS_LIMIT_THRESHOLD.amount_cents ?? 0;
    return (
      !isBrickAndMortar &&
      isAmerican &&
      (minAmountCents >= threshold || maxAmountCents > threshold)
    );
  })();

  const param = {
    country: retailerCountry,
  };
  const { data: creditCardLimitRangesResponse } =
    useCreditCardLimitRangesQuery(param);
  const creditCardLimitRanges =
    creditCardLimitRangesResponse?.credit_card_limit_ranges;
  const { data: fundingAmountRangesResponse } =
    useFundingAmountRangesQuery(param);
  const fundingAmountRanges =
    fundingAmountRangesResponse?.funding_amount_ranges;
  const { data: preferredTermsAmountRangesResponse } =
    usePreferredTermsAmountRangesQuery({
      ...param,
      isBrickAndMortar: isBrickAndMortar,
    });
  const preferredTermsAmountRanges =
    preferredTermsAmountRangesResponse?.preferred_terms_amount_ranges;
  const isPreferredTermsRequired = !isBrickAndMortar || isAnyTierActive;
  const shouldAskFicoScore = isAnyTierActive && !isBrickAndMortar && !isEurope;
  const shouldAskPreferredTerms =
    !isPreferredTermsRequired && !isSoftCreditCheckApplication;

  const requiredFinancialFieldKeys = useRequiredFinancialKeys({
    isSignUp,
    formData,
    requireCreditReport,
    isPreferredTermsRequired,
  });

  const visibleFinancialInfoKeys: OwFFormFieldKey[] = getVisibleFieldKeys(
    [
      "residential_address",
      "preferred_terms_amount",
      "funding_amount",
      "funding_sources",
      "credit_card_limit",
      "credit_check_authorized",
    ],
    requiredFinancialFieldKeys
  );

  if (shouldAskFicoScore) {
    const creditCheckIndex = visibleFinancialInfoKeys.findIndex(
      (element) => element === "credit_check_authorized"
    );
    if (creditCheckIndex >= 0) {
      visibleFinancialInfoKeys.splice(creditCheckIndex, 0, "fico_score");
    } else {
      visibleFinancialInfoKeys.push("fico_score");
    }
  }

  if (shouldAskPreferredTerms) {
    const residentialAddressIndex = visibleFinancialInfoKeys.findIndex(
      (element) => element === "residential_address"
    );
    visibleFinancialInfoKeys.splice(
      residentialAddressIndex + 1,
      0,
      "preferred_terms_amount"
    );
  }

  const getFundingAmountLabelText = () => {
    return isNMIApplication
      ? strictLocalize({
          defaultMessage:
            "How much money have you put aside for your business, including personal, family, investors, loans, or grants?",
          description: {
            text: "Open with Faire application question.",
            img: "https://cdn.faire.com/fastly/849e2da964ed33c1910769f309540b7788f7182277cec481028b616853637cb8.png",
          },
        })
      : strictLocalize({
          defaultMessage: "How much funding have you secured so far?",
          description: { text: "Open with Faire application question" },
        });
  };

  const getCreditCardLimitLabelText = () => {
    return strictLocalize({
      defaultMessage: "What’s the limit on your primary credit card?",
      description: { text: "Open with Faire application question" },
    });
  };

  const getFinancialInfoErrors = () =>
    validatePartialFormData({
      formData,
      keysToValidate: requiredFinancialFieldKeys,
      isResidentialAddressFormVisible,
    });

  const financialInfoFields: IOwFFormFieldSchema[] = visibleFinancialInfoKeys
    .map<IOwFFormFieldSchema | null>((key) => {
      switch (key) {
        case "preferred_terms_amount":
          return {
            fieldType: OwFFormFieldType.MONEY_RANGE_DROPDOWN,
            fieldName: "preferred_terms_amount",
            labelText: strictLocalize({
              defaultMessage: "How much in 60-day payment terms will you need?",
              description: { text: "Open with Faire application question" },
            }),

            placeholder: strictLocalize({
              defaultMessage: "Select preferred amount",
              description: {
                text: "Open with Faire application question. Allowing retailer to pick a desired terms limit amount",
              },
            }),
            helper: requireCreditReport
              ? strictLocalize(
                  {
                    defaultMessage:
                      "Limits over {threshold} require a soft credit check.",
                    description: {
                      text: "If retailer wants more terms limit, we run credit check",
                    },
                  },
                  DEPRECATED_backwardsCompatibleValues({
                    threshold: formatMoney({
                      money: CREDIT_CHECK_PREFERRED_TERMS_LIMIT_THRESHOLD,
                      roundingStrategy: RoundingStrategy.DROP_CENTS,
                    }),
                  })
                )
              : "",
            moneyRangeOptions: preferredTermsAmountRanges,
            required: isPreferredTermsRequired,
          };
        case "funding_amount":
          return {
            fieldType: OwFFormFieldType.MONEY_RANGE_DROPDOWN,
            fieldName: "funding_amount",
            labelText: getFundingAmountLabelText(),
            explanation: !isNMIApplication
              ? strictLocalize({
                  defaultMessage:
                    "E.g. personal investment, friends and family gift, bank loans, government grants and subsidies, etc.",
                  description: {
                    text: "Explaining what can be considered as funding sources",
                  },
                })
              : undefined,
            moneyRangeOptions: fundingAmountRanges,
            required: true,
          };
        case "funding_sources":
          return {
            fieldType: isAnyTierActive
              ? OwFFormFieldType.NESTED_CHECKBOXES
              : OwFFormFieldType.TEXTAREA,
            fieldName: "funding_sources",
            labelText: strictLocalize({
              defaultMessage: "Where have you received funding from?",
              description: { text: "Open with Faire application question" },
            }),
            required: true,
            nestedOptions: isAnyTierActive
              ? fundingOptions(isEurope, strictLocalize)
              : undefined,
          };
        case "credit_check_authorized":
          return {
            fieldType: OwFFormFieldType.CREDIT_CHECK_AUTH,
            fieldName: "credit_check_authorized",
            required: true,
          };
        case "fico_score":
          return {
            fieldType: OwFFormFieldType.DROPDOWN,
            fieldName: "fico_score",
            labelText: strictLocalize({
              defaultMessage: "What is your FICO score?",
              description: {
                text: "Input field for the Fair Isaac Corporation (FICO) credit score metric",
                img: "https://cdn.faire.com/fastly/93bf1f0f105c0cdc856b69784e386e16ca5e1414ed7a411fd53c9f843b14c11b.png",
              },
            }),
            options: FICO_SCORE_OPTIONS(strictLocalize),
            required: false,
          };
        case "residential_address":
          return buildResidentialAddressSchema({
            key,
            isResidentialAddressFormVisible,
            setValue,
            toggleResidentialAddressForm,
          });
        default:
        case "credit_card_limit":
          return {
            fieldType: OwFFormFieldType.MONEY_RANGE_DROPDOWN,
            fieldName: "credit_card_limit",
            labelText: getCreditCardLimitLabelText(),
            moneyRangeOptions: creditCardLimitRanges,
            required: true,
          };
      }
    })
    .filter(isNotNull);

  return {
    visibleFinancialInfoKeys,
    getFinancialInfoErrors,
    financialInfoFields,
  };
};

const useRequiredFinancialKeys = ({
  isSignUp,
  formData,
  isPreferredTermsRequired,
  requireCreditReport,
}: {
  isSignUp: boolean | undefined;
  formData: IRetailerLaunchWithFaireApplication;
  isPreferredTermsRequired: boolean;
  requireCreditReport: boolean;
}): Array<OwFFormFieldKey> => {
  const { isAnyTierActive } = useOwFIDVTierLogic();
  const { isNMIApplication } = useIsNMIApplication();
  const { isSoftCreditCheckApplication } = useIsSoftCreditCheckApplication();
  const requiredFinancialFieldKeys: OwFFormFieldKey[] = [
    "funding_amount",
    "credit_card_limit",
  ];

  if (isNMIApplication) {
    return ["credit_card_limit", "funding_amount"];
  }
  if (isSoftCreditCheckApplication) {
    return ["credit_check_authorized"];
  }
  if (isSignUp) {
    requiredFinancialFieldKeys.push("residential_address");
  }
  if (
    showFundingSources({
      ...formData,
      isSettingOn: isAnyTierActive,
    })
  ) {
    requiredFinancialFieldKeys.push("funding_sources");
  }
  if (isPreferredTermsRequired) {
    requiredFinancialFieldKeys.push("preferred_terms_amount");
  }
  if (requireCreditReport) {
    requiredFinancialFieldKeys.push("credit_check_authorized");
  }
  return requiredFinancialFieldKeys;
};
